import {
  ButtonMenu,
  Css,
  FilterDefs,
  GridDataRow,
  GridTableLayout,
  HasIdAndName,
  HeaderAction,
  IconButton,
  ModalProps,
  NestedOption,
  RowStyles,
  actionColumn,
  collapseColumn,
  column,
  emptyCell,
  multiFilter,
  selectColumn,
  simpleDataRows,
  simpleHeader,
  toggleFilter,
  treeFilter,
  useComputed,
  useGridTableApi,
  useGridTableLayoutState,
  useModal,
} from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { type NavigateFunction, useNavigate } from "react-router";
import { createTaskCatalogFolderUrl, createTaskCatalogFormUrl } from "src/RouteUrls";
import { GlobalPlanTaskFilter } from "src/generated/graphqlTypes";
import { definedFilterValues } from "src/routes/projects/schedule-v2/table/filterUtils";
import { TaskCatalogPageMetadataQuery, TaskCatalogPageMetadata_RegionFragment } from "./TaskCatalogPage.generated";
import {
  TaskCatalogTableQuery,
  TaskCatalogTable_GlobalPlanTagFragment,
  TaskCatalogTable_GlobalPlanTaskFragment,
  useTaskCatalogTableQuery,
} from "./TaskCatalogTable.generated";
import { AddGlobalPlanTagModal } from "./components/AddGlobalPlanTagModal";
import { ArchiveCatalogTaskModal } from "./components/ArchiveCatalogTaskModal";
import { BulkGlobalPlanTagsModal } from "./components/BulkGlobalPlanTagsModal";

type TaskCatalogTableProps = {
  businessFunctionTypes: TaskCatalogPageMetadataQuery["enumDetails"]["businessFunctionType"];
  /** Narrows the query to a single tag/team when rendered on a folder page. */
  folderFilter: Partial<GlobalPlanTaskFilter>;
  globalPlanTags: TaskCatalogTable_GlobalPlanTagFragment[];
  /** Folder pages hide the group-by control because they're already scoped to one team/tag. */
  isFolderPage: boolean;
  isReadOnly: boolean;
  regions: TaskCatalogPageMetadata_RegionFragment[];
};

export function TaskCatalogTable(props: TaskCatalogTableProps) {
  const { businessFunctionTypes, folderFilter, globalPlanTags, isFolderPage, isReadOnly, regions } = props;
  const filterDefs: FilterDefs<TaskCatalogFilter> = useMemo(
    () => ({
      team: multiFilter({
        label: "Team",
        options: businessFunctionTypes.sortByKey("name"),
        getOptionLabel: ({ name }) => name,
        getOptionValue: ({ code }) => code,
      }),
      project: treeFilter({
        label: "Specific to",
        options: getSpecifyToTreeSelectOptions(regions),
        filterBy: "all",
        getOptionLabel: (o) => o.name,
        getOptionValue: (o) => o.id,
      }),
      tags: multiFilter({
        label: "Tags",
        options: globalPlanTags.sortByKey("name"),
        getOptionLabel: ({ name }) => name,
        getOptionValue: ({ id }) => id,
      }),
      applyToAllSchedules: toggleFilter({ label: "Specific to All Schedules" }),
      archived: toggleFilter({ label: "Show Archived Tasks" }),
    }),
    [businessFunctionTypes, globalPlanTags, regions],
  );
  const layoutState = useGridTableLayoutState<TaskCatalogFilter>({
    persistedFilter: { filterDefs, storageKey: "taskCatalogFilter" },
    search: "server",
    groupBy: isFolderPage ? undefined : taskCatalogGroupByOptions,
  });
  const groupBy = (layoutState.groupBy?.value ?? TaskCatalogGroupBy.None) as TaskCatalogGroupBy;
  const query = useTaskCatalogTableQuery({
    variables: {
      filter: mapToFilter(layoutState.filter, layoutState.searchString, folderFilter),
      page: defaultTaskCatalogPage,
    },
  });
  const navigate = useNavigate();
  const { openModal } = useModal();
  const api = useGridTableApi<Row>();
  const selectedRows = useComputed(() => api.getSelectedRowIds("data"), [api]);
  // Grouped rows are prefixed with their folder id, e.g. `globalPlanTag:1-gpt:2`.
  const selectedTaskIds = useMemo(
    () => selectedRows.map((id) => (id.includes("-") ? id.split("-")[1] : id)),
    [selectedRows],
  );
  const maybeFetchNextPage = useCallback(async () => {
    if (query.data?.globalPlanTasks.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: query.data.globalPlanTasks.entities.length, limit: TASK_CATALOG_PAGE_SIZE } },
      });
    }
  }, [query]);
  const columns = useMemo(() => createColumns(openModal, navigate, isReadOnly), [openModal, navigate, isReadOnly]);
  const createTableRows = useCallback(
    (tableData: TaskCatalogTableQuery | undefined) =>
      createRows(tableData?.globalPlanTasks.entities ?? [], groupBy, globalPlanTags),
    [groupBy, globalPlanTags],
  );
  const actions: HeaderAction[] = useMemo(
    () =>
      isReadOnly
        ? []
        : [
            {
              kind: "menu",
              trigger: { icon: "verticalDots", variant: "outline" },
              tooltip: "Actions",
              items: [
                {
                  label: "Add to an Existing Tag",
                  onClick: () => openModal({ content: <BulkGlobalPlanTagsModal taskIds={selectedTaskIds} /> }),
                  disabled: selectedTaskIds.isEmpty,
                },
                {
                  label: "Add to a New Tag",
                  onClick: () => openModal({ content: <AddGlobalPlanTagModal taskIds={selectedTaskIds} /> }),
                  disabled: selectedTaskIds.isEmpty,
                },
              ],
            },
          ],
    [isReadOnly, openModal, selectedTaskIds],
  );

  return (
    <GridTableLayout
      hideEditColumns
      layoutState={layoutState}
      actions={actions}
      tableProps={{
        query,
        createRows: createTableRows,
        columns,
        api,
        rowStyles,
        as: "virtual",
        style: { grouped: groupBy !== TaskCatalogGroupBy.None },
        emptyFallback: "No tasks found.",
        infiniteScroll: { onEndReached: maybeFetchNextPage },
      }}
    />
  );
}

function getSpecifyToTreeSelectOptions(
  regions: TaskCatalogPageMetadata_RegionFragment[],
): NestedOption<HasIdAndName>[] {
  return regions
    .map((r) => ({
      ...r,
      children: r.markets.map((m) => ({
        ...m,
        children: m.communitites.map((c) => ({ ...c, children: c.subdivisions })),
      })),
    }))
    .sortBy((r) => r.name);
}

function mapToFilter(
  filter: TaskCatalogFilter,
  searchString: string | undefined,
  folderFilter: Partial<GlobalPlanTaskFilter>,
): GlobalPlanTaskFilter {
  const { archived, ...others } = filter;
  return {
    name: searchString ?? "",
    ...folderFilter,
    ...definedFilterValues(others),
    archived: archived ? undefined : false,
  };
}

function createColumns(openModal: (props: ModalProps) => void, navigate: NavigateFunction, isReadonly: boolean) {
  return [
    collapseColumn<Row>(),
    selectColumn<Row>(),
    column<Row>({ header: "Task Name", folder: ({ name }) => name, data: ({ name }) => name, w: 3, mw: "240px" }),
    column<Row>({
      header: "Description",
      folder: emptyCell,
      data: ({ description }) => description,
      w: 3,
      mw: "240px",
    }),
    column<Row>({
      header: "Variants",
      folder: emptyCell,
      data: ({ variants }) => <span css={Css.if(variants.isEmpty).gray400.$}>{variants.length}</span>,
      w: 1,
      mw: "100px",
    }),
    column<Row>({
      header: "Times Used",
      folder: emptyCell,
      data: ({ numberOfTimesUsed }) => numberOfTimesUsed,
      w: 1,
      mw: "120px",
    }),
    column<Row>({
      header: "Constraints",
      folder: emptyCell,
      data: ({ constraints }) => constraints.length,
      w: 1,
      mw: "120px",
    }),
    column<Row>({
      header: "Allowances",
      folder: emptyCell,
      data: ({ allowances }) => allowances.length,
      w: 1,
      mw: "120px",
    }),
    column<Row>({
      header: "Duration Variance",
      folder: emptyCell,
      data: ({ optimisticDurationInDays, pessimisticDurationInDays }) => {
        const spread = pessimisticDurationInDays - optimisticDurationInDays;
        return spread === 0 ? "—" : `±${spread}d`;
      },
      w: 1,
      mw: "160px",
    }),
    // TODO: implement — requires backend field on GlobalPlanTask
    actionColumn<Row>({
      header: emptyCell,
      folder: (data, { row }) => (
        <IconButton
          icon="chevronRight"
          onClick={() => {
            navigate(createTaskCatalogFolderUrl(row.id));
          }}
        />
      ),
      data: (row) =>
        isReadonly ? (
          emptyCell
        ) : (
          <ButtonMenu
            trigger={{ icon: "verticalDots" }}
            items={[
              { label: "Make a Copy", onClick: createTaskCatalogFormUrl("add", row.id) },
              {
                label: "Archive",
                onClick: () =>
                  openModal({ content: <ArchiveCatalogTaskModal catalogTaskName={row.name} catalogTaskId={row.id} /> }),
              },
            ]}
          />
        ),
      w: "48px",
    }),
  ];
}

function createRows(
  data: TaskCatalogTable_GlobalPlanTaskFragment[],
  groupBy: TaskCatalogGroupBy,
  globalPlanTags: TaskCatalogTable_GlobalPlanTagFragment[],
) {
  switch (groupBy) {
    case TaskCatalogGroupBy.None:
      return simpleDataRows(data);
    case TaskCatalogGroupBy.Team:
      return groupByTeam(data);
    case TaskCatalogGroupBy.Tag:
      return groupByTag(data, globalPlanTags);
    default:
      return simpleDataRows(data);
  }
}

function groupByTeam(data: TaskCatalogTable_GlobalPlanTaskFragment[]) {
  return [
    simpleHeader,
    ...data
      .groupByObject((task) => task.businessFunctionType)
      .map(([team, tasks]) => {
        return {
          kind: "folder" as const,
          id: team.code,
          data: { name: team.name ?? "Unknown", folderType: "team" as const },
          children: tasks.map((task) => ({ kind: "data" as const, data: task, id: `${team.code}-${task.id}` })),
        };
      }),
  ];
}

const rowStyles: RowStyles<Row> = { data: { rowLink: (row) => createTaskCatalogFormUrl(row.data.id) } };

function groupByTag(
  data: TaskCatalogTable_GlobalPlanTaskFragment[],
  globalPlanTags: TaskCatalogTable_GlobalPlanTagFragment[],
) {
  // group tasks by their tag id
  const groupedTasks = data.reduce<Record<string, GridDataRow<Row>[]>>(
    (groupedTasks, task) => {
      if (task.tags.isEmpty) {
        // If the task has no tags, we put it in the "No Tags" group
        return {
          ...groupedTasks,
          "no-tags": [
            ...(groupedTasks["no-tags"] || []),
            { kind: "data" as const, id: `no-tags-${task.id}`, data: task },
          ],
        };
      }
      task.tags.forEach((tag) => {
        const tagId = tag.id;
        groupedTasks[tagId] = [
          ...(groupedTasks[tagId] || []),
          { kind: "data" as const, id: `${tagId}-${task.id}`, data: task },
        ];
      });
      return groupedTasks;
    },
    globalPlanTags.reduce<Record<string, GridDataRow<Row>[]>>((acc, tag) => ({ ...acc, [tag.id]: [] }), {
      "no-tags": [],
    }),
  );

  return [
    simpleHeader,
    ...Object.entries(groupedTasks)
      // No Tags should always be last
      .sort(([a], [b]) => (a === "no-tags" ? 1 : b === "no-tags" ? -1 : a.localeCompare(b)))
      .map(([tagId, tasks]) => ({
        kind: "folder" as const,
        id: tagId ?? "no-tags",
        data: { name: globalPlanTags.find(({ id }) => id === tagId)?.name ?? "No Tags", folderType: "tag" as const },
        children: tasks,
      })),
  ];
}

enum TaskCatalogGroupBy {
  Team = "Team",
  Tag = "Tag",
  None = "None",
}

const taskCatalogGroupByOptions: Record<TaskCatalogGroupBy, string> = {
  [TaskCatalogGroupBy.None]: "None",
  [TaskCatalogGroupBy.Team]: "Team",
  [TaskCatalogGroupBy.Tag]: "Tag",
};

type TaskCatalogFilter = {
  team?: GlobalPlanTaskFilter["team"];
  project?: GlobalPlanTaskFilter["project"];
  tags?: GlobalPlanTaskFilter["tags"];
  applyToAllSchedules?: GlobalPlanTaskFilter["applyToAllSchedules"];
  archived?: GlobalPlanTaskFilter["archived"];
};

const TASK_CATALOG_PAGE_SIZE = 100;
/** Exported so stories/tests can key their mocks off the same page arg the table sends. */
export const defaultTaskCatalogPage = { offset: 0, limit: TASK_CATALOG_PAGE_SIZE };

type HeaderRow = { kind: "header" };
type FolderRow = { kind: "folder"; data: { name: string; folderType: "tag" | "team" } };
type DataRow = { kind: "data"; data: TaskCatalogTable_GlobalPlanTaskFragment };

type Row = HeaderRow | FolderRow | DataRow;
